import { AlertModal, PageWrapper } from "../../../components"
import {
  ContractSectionCards,
  ContractSummary,
  DesktopColumns,
  MobilePager,
} from "../../../domains/Contracts/components"
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useOpenInApp, useSharedDocument, useViewport } from "../../../hooks"

import arrowLeft from "../../../assets/icons/Arrows/arrow-narrow-left.png"
import { useTranslation } from "react-i18next"

const ShareDocument = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { loading, error, document } = useSharedDocument(token)
  const { showOpenAppBanner, setShowOpenAppBanner, handleOpenInApp } =
    useOpenInApp(token)

  const [alertOpen, setAlertOpen] = useState(false)
  const [showSummary, setShowSummary] = useState(false)
  const [sectionStatus, setSectionStatus] = useState({})
  const [page, setPage] = useState(0)
  const [activeSectionIndex, setActiveSectionIndex] = useState(0)
  const pagerRef = useRef(null)

  const { isMobile, viewportWidth, pageWidth } = useViewport(48)

  const analysis = document?.analysis || null

  const normalizedSections = useMemo(() => {
    const docId = token ?? "sharedDoc"
    const sections = analysis?.sections || []
    return sections.map((s, index) => ({
      ...s,
      id: s.id ?? `${docId}:${index}`,
    }))
  }, [analysis?.sections, token])

  // For shared documents, we don't have user interactions, so section status is empty
  useEffect(() => {
    setSectionStatus({})
  }, [])

  const handleShowSummary = () => setShowSummary(true)

  const handleHorizontalScroll = (e) => {
    const x = e.currentTarget.scrollLeft
    const newPage = Math.round(x / pageWidth)
    if (newPage !== page) setPage(newPage)
  }
  const renderLeftPane = () =>
    !showSummary ? (
      <ContractSectionCards
        sections={normalizedSections}
        sectionStatus={sectionStatus}
        onShowSummary={handleShowSummary}
        progressPercentage={100}
        activeSectionIndex={activeSectionIndex}
        onActiveSectionChange={setActiveSectionIndex}
        withoutEdit={true}
      />
    ) : (
      <ContractSummary
        setShowSummary={setShowSummary}
        summary={analysis?.summary}
        sections={analysis?.sections}
        sectionStatus={sectionStatus}
        withoutEdit={true}
      />
    )

  const leftPane = useMemo(
    () => renderLeftPane(),
    [showSummary, analysis, sectionStatus, 100, activeSectionIndex]
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-light-default flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t("share.loading")}</p>
        </div>
      </div>
    )
  }

  return (
    <PageWrapper
      header={
        <header className="w-full mx-auto mb-6 px-4 md:px-6 lg:px-6 flex items-center justify-between">
          <div className="flex-none">
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Back"
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/50 transition"
            >
              <img src={arrowLeft} alt="back" className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 text-center">
            <h1 className="text-[28px] font-bold text-dark-default truncate">
              {t("share.title")}
            </h1>
          </div>
          <div aria-hidden className="flex-none w-10 h-10" />
        </header>
      }
      variant="full"
      background="bg-light-default"
      className="px-0 h-screen overflow-hidden"
    >
      {showOpenAppBanner && (
        <div className="w-full top-0 z-50 absolute">
          <div className="mx-auto w-full bg-dark-default text-white px-4 py-3 flex items-center justify-between">
            <div className="text-sm sm:text-base font-medium pr-3">
              {t("share.openInApp")}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleOpenInApp}
                className="bg-white text-dark-default hover:bg-gray-100 transition-colors text-sm font-semibold px-3 py-2 rounded-lg"
              >
                {t("share.openInAppButton")}
              </button>
              <button
                onClick={() => setShowOpenAppBanner(false)}
                aria-label={"Close"}
                className="text-white/80 hover:text-white text-lg px-1"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full h-full overflow-hidden">
        {isMobile ? (
          <MobilePager
            leftPane={leftPane}
            pagerRef={pagerRef}
            page={page}
            pageWidth={pageWidth}
            viewportWidth={viewportWidth}
            document={document}
            onCloseDocument={() => navigate(-1)}
            handleHorizontalScroll={handleHorizontalScroll}
          />
        ) : (
          <DesktopColumns
            leftPane={leftPane}
            document={document}
            onCloseDocument={() => navigate(-1)}
          />
        )}
      </div>

      <AlertModal open={alertOpen} onClose={() => setAlertOpen(false)} />
    </PageWrapper>
  )
}

export default ShareDocument
